import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { BfAuthService } from '@brafton/skynet-angular-security-service';
import { FeedbackApiTokenStore, FeedbackRoundService } from '@brafton/feedback-api';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LOGIN_ROUTE } from './skynet-auth.config';

/**
 * Route guard for the authenticated app shell.
 *
 * 1. Blocks the shell until the org auth library reports a live session.
 *    `BfAuthService.authenticated()` is synchronous — it only checks the expiry
 *    of the `id_token` in localStorage — so unlike an SDK with an async session
 *    restore there is nothing to wait for here.
 * 2. Once authenticated, ensures a *usable* Feedback API token is present
 *    (fetched from `GET /v1/token`, best-effort) before allowing entry, so
 *    protected pages' API calls are authorized. The fetch stays behind the
 *    authentication check so the token endpoint is never hit for anonymous
 *    visitors. `FeedbackApiTokenStore.get()` reports an expired token as
 *    absent, so signing in again replaces one rather than reusing it.
 *
 * The API token is deliberately NOT the Auth0 token: no `audience` is requested,
 * so Auth0 issues an opaque access token the API could not validate, and the API
 * verifies its own HS256 tokens. See `core/skynet-auth.config.ts`.
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(BfAuthService);
  const router = inject(Router);
  const tokenStore = inject(FeedbackApiTokenStore);
  const feedbackRounds = inject(FeedbackRoundService);

  if (!auth.authenticated()) {
    // Skynet carries the return URL itself, in Auth0's `state` as `ru=<path>`,
    // captured when BF_AUTH_CONFIG_OPTIONS is built. `returnUrl` is kept only so
    // the login page can navigate back when a session already exists and Lock
    // never runs.
    return of(router.createUrlTree([LOGIN_ROUTE], { queryParams: { returnUrl: state.url } }));
  }

  // Authenticated. Reuse a stored token that is still live; otherwise fetch one
  // now. (Also cleared on logout, so a signed-out user retains no API access.)
  if (tokenStore.get()) {
    return of(true);
  }
  return feedbackRounds.getToken().pipe(
    map((response) => {
      if (response?.token) {
        tokenStore.set(response.token);
      }
      return true;
    }),
    // Best-effort: don't block entry if the token fetch fails (the app loads;
    // authenticated API calls simply fail until it succeeds).
    catchError(() => of(true)),
  );
};
